import { zodResolver } from '@hookform/resolvers/zod';
import { IUsageEvent, UsageEventUpdateReqSchema } from '@monetize/types/app';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useEditUsageRecord } from '../../../api/accountsService';
import { handleApiErrorToast } from '../../../api/axios';
import {
  MButton,
  MCenterModal,
  MCheckbox,
  MCustomNumberInput,
  MFormField,
  MStack,
} from '../../../components/Monetize';
import { useCurrencies } from '../../../hooks/useCurrencies';

export const EditUsageEventModal = ({
  usageEvent,
  onClose,
}: {
  usageEvent: IUsageEvent;
  onClose: () => void;
}) => {
  const [hasPriceOverride, setHasPriceOverride] = useState(
    () => usageEvent.ratedPrice != null,
  );
  const { currencyPlaceholder } = useCurrencies();
  const { mutateAsync: doEdit, isPending: isLoading } = useEditUsageRecord();

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = useForm({
    resolver: zodResolver(UsageEventUpdateReqSchema),
    mode: 'onChange',
    defaultValues: {
      unitsConsumed: usageEvent.unitsConsumed,
      ratedPrice: usageEvent.ratedPrice ?? null,
    },
  });

  const handleEdit = async (data: {
    unitsConsumed: number;
    ratedPrice?: number | null;
  }) => {
    try {
      await doEdit({
        id: usageEvent.id,
        payload: {
          unitsConsumed: data.unitsConsumed,
          ratedPrice:
            hasPriceOverride && (data.ratedPrice ?? 0) > 0
              ? (data.ratedPrice ?? null)
              : null,
        },
      });
      onClose();
    } catch (error) {
      handleApiErrorToast(error);
    }
  };

  return (
    <MCenterModal
      size="sm"
      isOpen
      modalTitle="Edit Usage Record"
      onClose={onClose}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
          <MButton
            onClick={onClose}
            variant="cancel"
            minW="auto"
            isDisabled={isLoading}
          >
            Cancel
          </MButton>
          <MButton
            onClick={handleSubmit(handleEdit)}
            isDisabled={!isValid || isLoading}
            isLoading={isLoading}
            type="submit"
            minW="auto"
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      <MStack gap={4}>
        <MFormField error={errors?.unitsConsumed} label="Unit(s)" isRequired>
          <Controller
            name="unitsConsumed"
            control={control}
            render={({ field: { onChange, ...rest } }) => (
              <MCustomNumberInput
                inputMode="decimal"
                placeholder="Enter Units"
                isDisabled={isLoading}
                onChange={(valueAsString: string) => onChange(valueAsString)}
                {...rest}
              />
            )}
          />
        </MFormField>
        <MFormField>
          <MCheckbox
            isChecked={hasPriceOverride}
            onChange={() => setHasPriceOverride(!hasPriceOverride)}
          >
            Override the price for this usage record
          </MCheckbox>
        </MFormField>
        {hasPriceOverride && (
          <MFormField error={errors?.ratedPrice} label="Amount Override">
            <Controller
              name="ratedPrice"
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MCustomNumberInput
                  value={value ?? undefined}
                  inputMode="decimal"
                  placeholder={currencyPlaceholder}
                  isDisabled={isLoading}
                  {...rest}
                />
              )}
            />
          </MFormField>
        )}
      </MStack>
    </MCenterModal>
  );
};
